import { createTheme } from '@mui/material/styles';
import { esES } from '@mui/material/locale';

/*
  Dark tokens (Fase A: no activar):
  // dark.primary.main: '#00DC7F'
  // dark.primary.contrastText: '#01332E'
  // dark.background.default: '#0B1412'
  // dark.background.paper: '#12201C'
  // dark.text.primary: '#E6E8E7'
*/

const ACERCATE_FONT =
  "'Plus Jakarta Sans', 'Manrope', 'Helvetica Neue', sans-serif";

export const acercateTheme = createTheme(
  {
    palette: {
      mode: 'light',
      primary: {
        main: '#016B61',
        contrastText: '#FFFFFF',
      },
      primaryContainer: {
        main: '#00DC7F',
        contrastText: '#01332E',
      },
      secondary: {
        main: '#C9A000',
        contrastText: '#201A00',
      },
      secondaryContainer: {
        main: '#F6C500',
        contrastText: '#3D3000',
      },
      tertiary: {
        main: '#BF5627',
        contrastText: '#FFFFFF',
      },
      tertiaryContainer: {
        main: '#FF844D',
        contrastText: '#2C0B00',
      },
      background: {
        default: '#F7FBF8',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#1A1C1B',
        secondary: '#404944',
      },
      error: {
        main: '#BA1A1A',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#1B7A4A',
        contrastText: '#FFFFFF',
      },
      successContainer: {
        main: '#D1F2E1',
        contrastText: '#0B301A',
      },
      warning: {
        main: '#C9A000',
        contrastText: '#201A00',
      },
      surfaceContainer: {
        main: '#E8F0EB',
        contrastText: '#1A1C1B',
      },
      inverseSurface: {
        main: '#2B322F',
        contrastText: '#F7FBF8',
      },
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: ACERCATE_FONT,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--acercate-primary': '#016B61',
            '--acercate-on-primary': '#FFFFFF',
            '--acercate-primary-container': '#00DC7F',
            '--acercate-on-primary-container': '#01332E',
            '--acercate-secondary': '#C9A000',
            '--acercate-on-secondary': '#201A00',
            '--acercate-secondary-container': '#F6C500',
            '--acercate-on-secondary-container': '#3D3000',
            '--acercate-tertiary': '#BF5627',
            '--acercate-on-tertiary': '#FFFFFF',
            '--acercate-tertiary-container': '#FF844D',
            '--acercate-on-tertiary-container': '#2C0B00',
            '--acercate-success-container': '#D1F2E1',
            '--acercate-on-success-container': '#0B301A',
            '--acercate-surface-container': '#E8F0EB',
            '--acercate-inverse-surface': '#2B322F',
            '--acercate-on-inverse-surface': '#F7FBF8',
          },
          body: {
            fontFamily: ACERCATE_FONT,
            backgroundColor: '#F7FBF8',
            color: '#1A1C1B',
          },
          '*:focus-visible': {
            outline: '2px solid #016B61',
            outlineOffset: 2,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          colorSecondary: {
            color: '#201A00',
            backgroundColor: '#F6C500',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
          },
          containedSecondary: {
            color: '#201A00',
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          color: 'inherit',
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            color: '#1A1C1B',
            borderBottom: '1px solid #D5E0D9',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: '#FFFFFF',
            borderRight: '1px solid #D5E0D9',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginInline: 8,
            minHeight: 40,
            '&.Mui-selected': {
              backgroundColor: '#00DC7F',
              color: '#01332E',
              '&:hover': {
                backgroundColor: '#00DC7F',
              },
              '& .MuiListItemIcon-root': {
                color: '#01332E',
              },
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: '#FFFFFF',
            borderRadius: 12,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            color: '#1A1C1B',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardSuccess: {
            backgroundColor: '#D1F2E1',
            color: '#0B301A',
          },
          filledSuccess: {
            backgroundColor: '#1B7A4A',
            color: '#FFFFFF',
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: '#2B322F',
            color: '#F7FBF8',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(26, 28, 27, 0.48)',
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          toolbar: {
            color: '#1A1C1B',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '0.7rem',
            padding: '5px',
          },
          head: {
            fontWeight: 600,
            fontSize: '0.8rem',
            backgroundColor: '#E8F0EB',
            color: '#1A1C1B',
          },
          body: {
            fontSize: '0.7rem',
          },
        },
      },
    },
  },
  esES,
);

export default acercateTheme;
